import cx from 'classnames';
import css from 'styled-jsx/css';
import PropTypes from 'prop-types';
import { BodyText as DesignBodyText } from '@lmig/lmds-react';
import breakpoints from '@lmig/lmds-tokens/dist/json/core/breakpoint.json';
import React from 'react';
import omitProps from "@lmig/dotcom-aspect-helpers/omitProps";

const getAlignStyles = ({ align = 'inherit' }) => {
  switch (align) {
    case 'center':
      return css.resolve`
        .lm-Body {
          text-align: center;
          margin-left: auto;
          margin-right: auto;
        }
      `;
    case 'right':
      return css.resolve`
        .lm-Body {
          text-align: right;
          margin-left: auto;
        }
      `;
    case 'left':
      return css.resolve`
        .lm-Body {
          text-align: left;
          margin-right: auto;
        }
      `;
    default:
      return { className: '', styles: '' };
  }
};

const getWidthStyles = ({ width }) => {
  switch (width) {
    case 'extraNarrow':
      return css.resolve`
        @import './global-styles/tokens';
        * {
          width: 14rem;
          padding-top: 0.75rem;
        }
        @media screen and (min-width: $lm-breakpoint-md-min ) {
          * {
            width: 100%;
            padding-top: 1.5rem;
          }
        }
      `;
    case 'narrow':
      return css.resolve`
        @import './global-styles/tokens';
        * {
          width: 17.8rem;
          padding-top: 0.5rem;
        }
        @media screen and (min-width: $lm-breakpoint-md-min ) {
          * {
            width: 100%;
          }
        }
      `;
    case 'mid-narrow':
      return css.resolve`
        @import './global-styles/tokens';
        @media screen and (min-width: 33.13rem ) {
          * {
            width: 29.4rem;
          }
        }
      `;
    default:
      return { className: '', styles: '' };
  }
};


const { className: superscriptClass, styles: superscriptStyles } = css.resolve`
  * :global(sup) {
    line-height: 0;
  }
`;

const getPaddingStyles = ({paddingStyle, paddingRight}) => {
  if (paddingStyle) {
    return css.resolve`
      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
        .lm-Body.lm-Body--article {
          padding-top: 4rem;
        }
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min}) {
        .lm-Body.lm-Body--article {
          padding-top: 5rem;
        }
      }

      @media screen and (min-width: 65.9rem) {
        .lm-Body.lm-Body--article {
          padding-top: 6rem;
        }
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.xl.min}) {
        .lm-Body.lm-Body--article {
          padding-top: 7rem;
        }
      }
    `;
  }

  if (paddingRight) {
    return css.resolve`
      @import './global-styles/tokens';
      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
        .lm-Body.lm-Body--article {
          padding-right: 1rem;
        }
      }
    `;
  }

  return { className: '', styles: '' };
};

const TextBody = ({
  align = 'inherit',
  className = '',
  children = undefined,
  width = '',
  paddingStyle = false,
  paddingRight = false,
  ...rest
}) => {
  const { className: alignClass, styles: alignStyles } = getAlignStyles({ align });
  const { className: widthClass, styles: widthStyles } = getWidthStyles({ width });
  const { className: paddingClass, styles: paddingStyles } = getPaddingStyles({paddingStyle, paddingRight});
  const filteredRestProps = omitProps(rest);

  const updatedClassName = cx(alignClass, superscriptClass, widthClass, paddingClass, className);

  return (
    <>
      <DesignBodyText className={updatedClassName} {...filteredRestProps}>
        {children}
      </DesignBodyText>
      {alignStyles}
      {superscriptStyles}
      {widthStyles}
      {paddingStyles}
    </>
  );
};

TextBody.propTypes = {
  ...DesignBodyText.propTypes,
  /** Text position */
  align: PropTypes.oneOf(['inherit', 'left', 'center', 'right']),
  /** Add custom classes to this element. */
  className: PropTypes.string,
  /** Body content is not automatically wrapped in semantic tags. Pass your content wrapped in the appropriate <p> or other text tags. */
  children: PropTypes.node,
  /** Set the width of the TextBody. */
  width: PropTypes.oneOf(['', 'extraNarrow', 'narrow', 'mid-narrow']),
  paddingStyle: PropTypes.bool,
  paddingRight: PropTypes.bool,
};

export default TextBody;
